import fetch from 'cross-fetch';
import { isEmpty } from 'ramda';
import { from, split } from 'apollo-link';
import { BatchHttpLink } from 'apollo-link-batch-http';
import apolloLogger from 'apollo-link-logger';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { onError } from 'apollo-link-error';
import { print } from 'graphql/language/printer';

import { getEnvironment, graphQLUrl } from '../utils';
import { logger } from '../logger';
import { fragmentTypes as possibleTypes } from '../../../fragmentTypes.mjs';
import { featuresLink } from '../experimentation';

import { isLogFiltered } from './helpers/is-log-filtered';

const { LOG_GRAPHQL_QUERIES } = getEnvironment();

/**
 *
 * @param {*} options
 * @param {*} param1
 * @returns {ApolloClient}
 */
export const createClient = (options, { initialState, ctx }) => {
  const cache = new InMemoryCache({
    possibleTypes,
    typePolicies: {
      // No id of its own - one per game. Merge, rather than replace, so
      // queries selecting different fields of it don't wipe each other's
      // fields out of the cached game.
      FalkorSuperchargerGameMessaging: { merge: true },
    },
  });
  const headers = {
    'X-Web-App': 'hub',
  };

  if (ctx && ctx.state.newAuthCookies && !isEmpty(ctx.state.newAuthCookies)) {
    headers.cookie = ctx.state.newAuthCookies.join(';');
  }

  initialState && cache.restore(initialState);
  const error = onError(({ operation, graphQLErrors }) => {
    if (isLogFiltered({ operation, graphQLErrors })) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log(`[GraphQL operation error] ${operation.operationName}`, {
      errors: graphQLErrors,
      query: print(operation.query),
    });
    logger.error(`[GraphQL operation error] ${operation.operationName}`, {
      errors: graphQLErrors,
      query: print(operation.query),
    });
  });

  const linkOptions = {
    uri: graphQLUrl,
    credentials: 'include',
    fetch,
    headers,
  };
  const batch = new BatchHttpLink(linkOptions);
  const upload = createUploadLink(linkOptions);
  const batchLink = from([error, batch]);
  const uploadLink = from([error, upload]);
  const links = [
    featuresLink,
    split(
      (operation) => operation.getContext().hasUpload,
      uploadLink,
      batchLink
    ),
  ];
  LOG_GRAPHQL_QUERIES == 'true' && links.unshift(apolloLogger);

  const link = from(links);

  return new ApolloClient({
    link,
    cache,
    dataIdFromObject: ({ id, __typename }) =>
      __typename && id ? `${__typename}:${id}` : null,
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
    },
    ...options,
  });
};
